import Vue from 'vue';
import { GetterTree, MutationTree } from 'vuex';
import actions from '@/store/campaignCreativesAPI';
import {
  CreativeFormat,
  ICampaignCreativesState,
  ICreative,
  IShowcase,
  ICreativeTemplate,
  IShowcaseTemplate,
} from '@/types/campaign/creatives';
import { Creative, DataProvider, RootState } from '@/types';
import { cloneDeep, camelCase } from 'lodash';

const state: ICampaignCreativesState = {
  allCreatives: {
    byId: {},
    allIds: [],
  },
  showcases: {
    byId: {},
    allIds: [],
  },
  editCreatives: {
    byId: {},
    allIds: [],
  },
  campaignCreativeIds: [],
  selectCreativeIds: [],
  editShowcaseId: null,
  isSelectShowcases: false,
  multipleFields: {},
  otherCreatives: {
    paginationData: null,
    data: [] as Array<Creative>,
  },
  creativeFormats: {
    paginationData: null,
    data: [] as Array<CreativeFormat>,
  },
  creativeTemplates: {
    paginationData: null,
    data: [] as Array<ICreativeTemplate>,
  },
  showcaseTemplates: {
    paginationData: null,
    data: [] as Array<IShowcaseTemplate>,
  },
  moderateCreatives: {
    paginationData: null,
    data: [] as Array<Creative>,
  },
};

const mutations: MutationTree<ICampaignCreativesState> = {
  /* All creatives */
  addCreativeToAllCreatives(state, creative: ICreative) {
    if (creative.id) {
      Vue.set(state.allCreatives.byId, creative.id, cloneDeep(creative));
      state.allCreatives.allIds.push(creative.id);
    }
  },
  deleteCreativeFromAllCreatives(state, creativeId: string) {
    delete state.allCreatives.byId[creativeId];
    state.allCreatives.allIds = state.allCreatives.allIds.filter(
      (id) => id !== creativeId
    );
  },
  updateCreativeInAllCreatives(state, creative: ICreative) {
    if (state.allCreatives.allIds.includes(creative.id)) {
      state.allCreatives.byId[creative.id] = cloneDeep(creative);
    }
  },
  changeCreativeStatus(state, payload) {
    const [creativeId, status] = payload;

    state.allCreatives.byId[creativeId].status = { ...status };
  },
  addPositionToCreative(state, payload) {
    const [creativeId, position] = payload;

    state.allCreatives.byId[creativeId].position = position;
  },
  clearPositionFromCreative(state, creativeId: string) {
    state.allCreatives.byId[creativeId].position = null;
  },
  clearCreativeStore(state) {
    state.allCreatives = {
      byId: {},
      allIds: [],
    };
    state.showcases = {
      byId: {},
      allIds: [],
    };
    state.editCreatives = {
      byId: {},
      allIds: [],
    };
    state.campaignCreativeIds = [];
    state.selectCreativeIds = [];
    state.editShowcaseId = null;
    state.isSelectShowcases = false;
  },

  /* Showcases */
  addShowcaseToShowcases(state, showcase: IShowcase) {
    if (showcase.id) {
      Vue.set(state.showcases.byId, showcase.id, cloneDeep(showcase));
      state.showcases.allIds.push(showcase.id);
    }
  },
  updateShowcaseInShowcases(state, showcase: IShowcase) {
    if (state.showcases.allIds.includes(showcase.id)) {
      state.showcases.byId[showcase.id] = cloneDeep(showcase);
    }
  },
  deleteShowcaseFromShowcases(state, showcaseId: string) {
    delete state.showcases.byId[showcaseId];
    state.showcases.allIds = state.showcases.allIds.filter(
      (id) => id !== showcaseId
    );
  },
  addCreativeToShowcase(state, payload) {
    const [showcaseId, creative] = payload;

    if (creative.position) {
      state.showcases.byId[showcaseId].creatives[creative.position - 1] =
        creative.id;
    } else {
      state.showcases.byId[showcaseId].creatives.push(creative.id);
    }
  },
  removeCreativeFromShowcase(state, payload) {
    const [showcaseId, creativeId] = payload;

    state.showcases.byId[showcaseId].creatives = state.showcases.byId[
      showcaseId
    ].creatives.filter((id) => id !== creativeId);
  },
  replaceCreativesInShowcase(state, payload) {
    const [showcaseId, creativeIds] = payload;

    state.showcases.byId[showcaseId].creatives = [...creativeIds];
  },
  changeShowcaseStatus(state, payload) {
    const [showcaseId, status] = payload;

    state.showcases.byId[showcaseId].status = { ...status };
  },

  /* Edit showcase id */
  addShowcaseToEditShowcaseId(state, showcaseId) {
    state.editShowcaseId = showcaseId;
  },
  removeShowcaseFromEditShowcaseId(state) {
    state.editShowcaseId = null;
  },

  /* Edit creatives */
  addCreativeToEditCreatives(state, creative) {
    if (creative.id) {
      Vue.set(state.editCreatives.byId, creative.id, cloneDeep(creative));
      state.editCreatives.allIds.push(creative.id);
    }
  },
  clearEditCreatives(state) {
    state.editCreatives = {
      byId: {},
      allIds: [],
    };
  },

  /* Campaign creative ids */
  addCreativeToCampaignCreatives(state, creativeId) {
    state.campaignCreativeIds.push(creativeId);
  },
  removeCreativeFromCampaignCreatives(state, creativeId) {
    state.campaignCreativeIds = state.campaignCreativeIds.filter(
      (id) => id !== creativeId
    );
  },
  replaceCreativesInCampaignCreatives(state, creativeIds: Array<string>) {
    state.campaignCreativeIds = [...creativeIds];
  },

  /* Select creative ids */
  addCreativeToSelectCreatives(state, creativeId) {
    state.selectCreativeIds.push(creativeId);
  },
  removeCreativeFromSelectCreatives(state, creativeId) {
    state.selectCreativeIds = state.selectCreativeIds.filter(
      (id) => id !== creativeId
    );
  },
  replaceCreativesInSelectCreatives(state, creativeIds: Array<string>) {
    state.selectCreativeIds = [...creativeIds];
  },
  clearSelectCreatives(state) {
    state.selectCreativeIds = [];
  },

  /* Is select showcases */
  openSelectShowcases(state) {
    state.isSelectShowcases = true;
  },
  closeSelectShowcases(state) {
    state.isSelectShowcases = false;
  },

  setOtherCreatives(state, data: DataProvider) {
    if (!data.paginationData || !data.paginationData?.currentPage) {
      return;
    }
    state.otherCreatives.paginationData = data.paginationData;
    if (+data.paginationData.currentPage == 1) {
      state.otherCreatives.data = data.data;
    } else {
      data.data.map((item: Creative) => {
        state.otherCreatives.data.push(item);
      });
    }
  },
  setCreativeFormats(state, data: DataProvider): void {
    if (!data.paginationData || !data.paginationData?.currentPage) {
      return;
    }
    state.creativeFormats.paginationData = data.paginationData;
    state.creativeFormats.data = data.data;
  },
  setCreativeTemplates(state, creative: DataProvider): void {
    if (!creative.paginationData || !creative.paginationData?.currentPage) {
      return;
    }
    state.creativeTemplates.paginationData = creative.paginationData;
    state.creativeTemplates.data = creative.data;
  },
  setShowcaseTemplates(state, showcase: DataProvider): void {
    if (!showcase.paginationData || !showcase.paginationData?.currentPage) {
      return;
    }
    state.showcaseTemplates.paginationData = showcase.paginationData;
    state.showcaseTemplates.data = showcase.data;
  },
  setModerateCreatives(state, data: DataProvider): void {
    if (!data.paginationData || !data.paginationData?.currentPage) {
      return;
    }
    state.moderateCreatives.paginationData = data.paginationData;
    state.moderateCreatives.data = data.data;
  },
};

const getters: GetterTree<ICampaignCreativesState, RootState> = {
  getAllCreatives(state) {
    return state.allCreatives;
  },
  getAllCreativesHaveErrors(state) {
    return state.allCreatives.allIds.filter((id) => {
      for (const field in state.allCreatives.byId[id].errors) {
        if (state.allCreatives.byId[id].errors[field].length) {
          return id;
        }
      }
    });
  },
  getShowcases(state) {
    return state.showcases;
  },
  getEditCreatives(state) {
    return state.editCreatives;
  },
  getEditCreativesHaveErrors(state) {
    return state.editCreatives.allIds.filter((id) => {
      for (const field in state.editCreatives.byId[id].errors) {
        if (state.editCreatives.byId[id].errors[field].length) {
          return id;
        }
      }
    });
  },
  getCampaignCreativeIds(state) {
    return state.campaignCreativeIds.map((id) => id);
  },
  getSelectCreativeIds(state) {
    return state.selectCreativeIds.map((id) => id);
  },
  getEditShowcaseId(state) {
    return state.editShowcaseId;
  },
  getIsSelectShowcases(state) {
    return state.isSelectShowcases;
  },
  getCreativeFormats(state) {
    return state.creativeFormats.data.map((cf): CreativeFormat => {
      return {
        isActive: false,
        width: cf.width,
        height: cf.height,
      };
    });
  },
  getOtherCreatives(state) {
    return state.otherCreatives;
  },
  getCreativeTemplates: (state) => {
    return state.creativeTemplates.data.map((template) => {
      template.fields = template.fields.map((field) => camelCase(field));
      return template;
    });
  },
  getShowcaseTemplates: (state) => {
    return state.showcaseTemplates.data.map((template) => template);
  },
  getModerateCreatives(state) {
    return state.moderateCreatives;
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions,
};
